import { useIsSplitEnabled, useSplitEnabled } from '@buffer-mono/features'

/**
 * Gates the removal of every customer-facing reference to Analyze (EPD-1939).
 *
 * While off, the product keeps its Analyze entry points: the "Go to Analyze"
 * links in Insights, the "More analytics" buttons on post cards, the `G A`
 * shortcut, and the `/analyze` redirect out to analyze.buffer.com. While on,
 * each of those either disappears or points at in-app Insights instead.
 *
 * Internal identifiers — URL builders, tracking names, Split names — are out
 * of scope. This flag only governs what a customer can see.
 */
export const ANALYZE_SUNSET_FLAG = 'analyze-sunset'

/**
 * Every call site that only *hides* an Analyze entry point. `isEnabled`
 * defaults to `false` while the SDK loads, which keeps today's behaviour, so
 * these need no readiness check.
 */
export function useAnalyzeSunsetEnabled(): boolean {
  return useIsSplitEnabled(ANALYZE_SUNSET_FLAG)
}

/**
 * Whether an Analyze fallback may still be shown.
 *
 * Reading the plain `useAnalyzeSunsetEnabled()` here would surface a link to a
 * product we are retiring, to exactly the customers it is retired for, for as
 * long as the SDK takes to answer — the pre-ready value is `false`, which reads
 * as "sunset is off". Requiring `isReady` costs flag-off customers a brief
 * absence of a secondary CTA instead, which is the cheaper of the two errors
 * and shrinks to nothing as the flag ramps.
 */
export function useCanShowAnalyzeFallback(): boolean {
  const { isEnabled, isReady } = useAnalyzeSunsetReady()
  return isReady && !isEnabled
}

/**
 * Ready-aware variant for the `/analyze` route guard, which acts on its very
 * first render. Reading the pre-ready `false` there would send a sunset
 * customer straight out to analyze.buffer.com — the one thing this flag exists
 * to stop — and the external navigation can't be undone once the real value
 * lands. Callers must hold off until `isReady`.
 */
export function useAnalyzeSunsetReady(): {
  isEnabled: boolean
  isReady: boolean
} {
  const { isEnabled, isReady } = useSplitEnabled(ANALYZE_SUNSET_FLAG)
  return { isEnabled, isReady }
}
